import { Button, Layout } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import IconEmpty from '../../../assets/Images/icons/empty';
import { ROUTER } from '../../../constants/router';
import useNotify from '../../../context/Notification/useNotify';
import { useForm } from 'antd/lib/form/Form';
import DocTabs from './component/DocTabs';
import HRPolicyService from '../../../services/HRPolicy.service';
import ListPrivacy from '../Shared/ListPrivacy';
import { usePreviewtoString } from './component/PreviewToString';
import { PlusOutlined } from '@ant-design/icons';
import GlobalService from '../../../services/Global.service';
import { calculateImageWidth } from '../../../util/util';
import usePermission from '../../../hooks/usePermission';

export default function HRPrivacyPolicy({ selectType }) {
    const { t, i18n } = useTranslation(['translation', 'hr-policy']);
    let lang = i18n.language;
    const [data, setData] = useState([]);
    const [currentSelectList, setCurrentSelectList] = useState();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(false);
    const [detailPublished, setDetailPublished] = useState();

    let navigate = useNavigate();
    // Logo
    const [logo, setLogo] = useState('');
    const [logoPublish, setLogoPublish] = useState('');
    const getImage = async (companyLogoId) => {
        try {
            const { data } = await GlobalService.getImage(companyLogoId);
            if (data.resultData?.imageId) {
                return `data:image/png;base64,${data.resultData?.imageId}`;
            }
        } catch (error) {
            setLogo('');
            setLogoPublish('');
            console.log(error);
        }
    };

    const getPolicyList = async () => {
        const { data } = await HRPolicyService.searchHRPolicy(
            selectType ? selectType : ''
        );
        if (data?.resultData) {
            setData(data.resultData);
            setCurrentSelectList(data.resultData[0]);
        }
    };

    useEffect(() => {
        const getHRPolicyDetail = async () => {
            try {
                const { data } = await HRPolicyService.getHRPolicyDetail(
                    currentSelectList?.hrPrivacyPolicyNoticeId
                );
                if (data?.resultData) {
                    setDetail(data?.resultData);
                    setLoading(false);
                    // Logo
                    setLogo('');
                    if (data?.resultData?.hrPrivacyPolicy?.hrCompanyLogoId) {
                        getImage(
                            data?.resultData?.hrPrivacyPolicy?.hrCompanyLogoId
                        ).then((logo) => setLogo(logo));
                    }
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.log(error);
                setLoading(false);
            }
        };

        const getPublishedVersion = async () => {
            try {
                const { data } = await HRPolicyService.getPublishedVersion(
                    currentSelectList?.hrPrivacyPolicyNoticeId
                );
                if (data?.resultData) {
                    const parse = JSON.parse(data?.resultData?.data);
                    setDetailPublished(parse);
                    // Logo
                    setLogoPublish('');
                    if (parse?.hrPrivacyPolicy?.hrCompanyLogoId) {
                        getImage(parse?.hrPrivacyPolicy?.hrCompanyLogoId).then(
                            (logo) => setLogoPublish(logo)
                        );
                    }
                }
            } catch (error) {
                console.log(error);
            }
        };
        if (currentSelectList?.hrPrivacyPolicyNoticeId) getHRPolicyDetail();
        if (
            currentSelectList?.hasChanged &&
            currentSelectList?.status === 'Published'
        )
            getPublishedVersion();
        else setDetailPublished();
    }, [currentSelectList]);

    useEffect(() => {
        getPolicyList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectType]);

    const notify = useNotify();
    const [form] = useForm();

    const handelRename = async (values) => {
        setLoading(true);
        if (currentSelectList?.hrPrivacyPolicyNoticeId) {
            const payload = {
                hrPrivacyPolicyId: currentSelectList?.hrPrivacyPolicyNoticeId,
                hrPrivacyPolicyNameTh: values.nameTh,
                hrPrivacyPolicyNameEn: values.nameEn,
            };
            setTimeout(async () => {
                try {
                    const { data } = await HRPolicyService.renameHRPolicy(
                        payload
                    );
                    if (data.resultCode === 1) {
                        form.resetFields();
                        notify({
                            title: t('common.Success'),
                            description: t('Successfully saved data'),
                            timeOut: 1800,
                        });
                        setTimeout(() => {
                            setLoading(false);
                            getPolicyList();
                        }, 1300);
                    } else {
                        setLoading(false);

                        notify({
                            type: 'error',
                            title: t('common.Fail'),
                            description: t('common.FailedSaveData'),
                        });
                    }
                } catch (error) {
                    setLoading(false);
                    notify({
                        type: 'error',
                        title: t('common.Fail'),
                        description: t('Failed to rename'),
                    });
                }
            }, 1000);
        }
    };
    const width1 = calculateImageWidth(logo);
    const width2 = calculateImageWidth(logoPublish);
    const { html } = usePreviewtoString(detail, 'TH', logo, width1);
    const { html: htmlEn } = usePreviewtoString(detail, 'EN', logo, width1);

    const { html: htmlPublish } = usePreviewtoString(
        detailPublished,
        'TH',
        logoPublish,
        width2
    );
    const { html: htmlPublishEn } = usePreviewtoString(
        detailPublished,
        'EN',
        logoPublish,
        width2
    );

    const { isAuditor } = usePermission();

    return (
        <Layout>
            <div className="flex items-center my-4">
                <div className="flex-1 font-bold text-xl">
                    {t('HR Privacy Policy list', {
                        ns: 'hr-policy',
                    })}
                </div>
                <Button
                    disabled={isAuditor}
                    type="primary"
                    size="large"
                    onClick={() => {
                        navigate(`${ROUTER.POLICY.HR_PRIVACY}/new#step1`);
                    }}
                    icon={<PlusOutlined />}
                >
                    {t('Add Policy')}
                </Button>
            </div>
            {true ? (
                <div className="grid grid-cols-7 gap-4">
                    <ListPrivacy
                        data={data}
                        pathEdit={ROUTER.POLICY.HR_PRIVACY}
                        stepEdit={'#step9'} // step preview
                        currentSelectName={{
                            nameTh: detail?.hrPrivacyPolicy
                                ?.hrPrivacyPolicyNameTh,
                            nameEn: detail?.hrPrivacyPolicy
                                ?.hrPrivacyPolicyNameEn,
                            namePublishTh:
                                detailPublished?.hrPrivacyPolicy
                                    ?.hrPrivacyPolicyNameTh,
                            namePublishEn:
                                detailPublished?.hrPrivacyPolicy
                                    ?.hrPrivacyPolicyNameEn,
                            placeholderNameEN: t('PlaceholderNameEN', {
                                ns: 'hr-policy',
                            }),
                            placeholderNameTH: t('PlaceholderNameTH', {
                                ns: 'hr-policy',
                            }),
                        }}
                        setCurrentSelectList={setCurrentSelectList}
                        currentSelectList={currentSelectList}
                        form={form}
                        // Parameters Name
                        policyObject={{
                            privacyName:
                                lang === 'en'
                                    ? 'hrPrivacyPolicyNoticeNameEn'
                                    : 'hrPrivacyPolicyNoticeNameTh',
                            nameTh: 'hrPrivacyPolicyNoticeNameTh',
                            nameEn: 'hrPrivacyPolicyNoticeNameEn',
                            privacyType:
                                lang === 'en'
                                    ? 'hrPrivacyPolicyTypeEn'
                                    : 'hrPrivacyPolicyTypeTh',
                            privacyId: 'hrPrivacyPolicyNoticeId',
                            privacyCompany:
                                lang === 'en'
                                    ? 'hrCompanyNameEn'
                                    : 'hrCompanyNameTh',
                        }}
                        handelRename={(value) => handelRename(value)}
                        loading={loading}
                        linkName="external-hr-privacy"
                        getPolicyList={getPolicyList}
                        historyObject={{
                            type: 'hr',
                            serviceHistory: HRPolicyService.getHistories,
                            historyParamName: 'hrPrivacyPolicyId', // ชื่อ payload ของ id ที่ service get history เช่น hrPrivacyPolicyId: "1234" ที่ต้องใส่คือ: hrPrivacyPolicyId
                            historyResultIdName: 'hrPrivacyPolicyHistoryId', // ได้หลังจาก service get history ใน result ชื่อ parameter ของ id เช่น { "hrPrivacyPolicyHistoryId" : 1234 } ที่ต้องใส่คือ: hrPrivacyPolicyHistoryId
                        }}
                        // pop up ของ confirm ก่อน delete
                        confirmObject={{
                            title:
                                lang === 'en'
                                    ? 'There may be websites that use this HR privacy policy.'
                                    : 'อาจมีเว็บไซต์ใช้นโยบายความเป็นส่วนตัว\nฝ่ายบุคคลนี้อยู่',
                            text:
                                lang === 'en'
                                    ? `Before deleting the data, please check that no website is using this HR privacy policy.`
                                    : `ก่อนลบข้อมูล โปรดตรวจสอบว่าไม่มีเว็บไซต์ใดใช้นโยบาย\nความเป็นส่วนตัวฝ่ายบุคคลนี้`,
                        }}
                        // pop up ของ delete
                        deleteObject={{
                            serviceDelete: HRPolicyService.deleteHRPolicy,
                            title:
                                currentSelectList?.status === 'Draft'
                                    ? t('ModalTitleDeleteDraft', {
                                          ns: 'hr-policy',
                                      })
                                    : t('ModalTitleDeleteDraft', {
                                          ns: 'hr-policy',
                                      }),
                            text:
                                currentSelectList?.status === 'Draft'
                                    ? `${t('ModalDeleteDraft', {
                                          version: currentSelectList?.version,
                                          ns: 'hr-policy',
                                      })}`
                                    : `${t('ModalDetailDeletePublish', {
                                          ns: 'hr-policy',
                                      })} \n${t('ModalDeletePublish', {
                                          version:
                                              currentSelectList?.latestPublishVersion,
                                          ns: 'hr-policy',
                                      })}`,
                        }}
                        templateBody={{
                            html,
                            htmlEn,
                            htmlPublish,
                            htmlPublishEn,
                        }}
                    >
                        {(previewLang) => (
                            <DocTabs
                                logo={logo}
                                logoPublish={logoPublish}
                                detail={detail}
                                loading={loading}
                                currentSelectList={currentSelectList}
                                previewLang={previewLang}
                                privacyPolicyNoticeId={
                                    currentSelectList?.hrPrivacyPolicyNoticeId
                                }
                                detailPublished={detailPublished}
                            />
                        )}
                    </ListPrivacy>
                </div>
            ) : (
                <div className="grid items-center justify-center mt-20">
                    <IconEmpty />
                </div>
            )}
        </Layout>
    );
}
